import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {setWindowTitle} from 'BenchlingUtil';
import {writeQuery} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import HelpButton from 'help/HelpButton';
import notifications from 'notifications';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {Header1} from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ToggleGroup from 'ui-library/ToggleGroup';
import * as Modals from 'util/Modals';

import {LabelPrintingSettingsQuery} from './__generated/LabelPrintingSettingsQuery';
import BartenderServerTable from './BartenderServerTable';
import {BartenderServer, makePlainBartenderServer} from './BartenderUtil';
import EditablePrintMethod from './EditablePrintMethod';
import {LabelPrintingMethod} from './LabelPrintingConstants';
import {putLabelPrintingConfig} from './LabelPrintingDAO';

type TenantAdminDataOrInaccessibleInfoNode = LabelPrintingSettingsQuery['currentTenant']['tenantAdminData'];

type TenantAdminDataNode = ExcludeInaccessible<TenantAdminDataOrInaccessibleInfoNode>;

export const LABEL_PRINTING_SETTINGS_QUERY = gql`
  query LabelPrintingSettingsQuery @skipAudit @engComponent(name: INVENTORY) {
    currentTenant {
      id
      name
      tenantAdminData {
        ... on TenantAdminDataNode {
          id
          configOverrides(
            keys: [
              "INVENTORY_LABEL_PRINTING_METHOD"
              "INVENTORY_BARTENDER_SERVERS"
              "INVENTORY_ENABLE_ZPL_LABEL_PREVIEW"
            ]
          ) {
            id
          }
        }
      }
      permissions {
        admin
      }
    }
    labelPrintingConfig {
      id
      tenantConfiguredPrintingMethod
      bartenderServers {
        id
        name
        url
      }
      enableLabelTemplatePreview
    }
  }
`;

// Exported only for testing
export enum LabelTemplatePreviewStatus {
  ON = 'ON',
  OFF = 'OFF',
}

function LabelPrintingSettingsPageContent({
  conf,
  onPersistChanges,
}: {
  conf: LabelPrintingSettingsQuery;
  onPersistChanges: (settings: {
    labelPrintingMethod: LabelPrintingMethod;
    bartenderServers: Array<BartenderServer>;
    enableLabelTemplatePreview: Boolean;
  }) => Promise<void>;
}): JSX.Element {
  const {
    currentTenant,
    labelPrintingConfig: {tenantConfiguredPrintingMethod, bartenderServers, enableLabelTemplatePreview},
  } = conf;

  const isWritable = Boolean(currentTenant.permissions.admin);

  const handleChangePrintingMethod = (newPrintingMethod: LabelPrintingMethod): void => {
    writeQuery(
      {query: LABEL_PRINTING_SETTINGS_QUERY},
      {
        ...conf,
        labelPrintingConfig: {
          ...conf.labelPrintingConfig,
          tenantConfiguredPrintingMethod: newPrintingMethod,
        },
      }
    );
    onPersistChanges({
      labelPrintingMethod: newPrintingMethod,
      bartenderServers: bartenderServers.map((bs) => makePlainBartenderServer(bs)),
      enableLabelTemplatePreview,
    });
  };

  const setEnableLabelPreview = (newEnableLabelTemplatePreview: Boolean): void => {
    writeQuery(
      {query: LABEL_PRINTING_SETTINGS_QUERY},
      {
        ...conf,
        labelPrintingConfig: {
          ...conf.labelPrintingConfig,
          enableLabelTemplatePreview: newEnableLabelTemplatePreview,
        },
      }
    );
    onPersistChanges({
      labelPrintingMethod: tenantConfiguredPrintingMethod,
      bartenderServers: bartenderServers.map((bs) => makePlainBartenderServer(bs)),
      enableLabelTemplatePreview: newEnableLabelTemplatePreview,
    });
  };

  const handleChangeLabelPreview = (newStatus: LabelTemplatePreviewStatus): void => {
    if (newStatus === LabelTemplatePreviewStatus.ON) {
      Modals.confirm(
        'Enable Label Template Preview',
        <>
          Before enabling this feature, ensure your labels do not contain sensitive information as it requires
          a third-party application to generate previews.&nbsp;
          <a
            href="https://help.benchling.com/hc/en-us/articles/9684246740493-Label-Printing-with-Zebra-Browser-Print#:~:text=the%20perfect%20prints!-,Preview%20Labels,-Once%20you%20have"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </>
      ).done(() => {
        setEnableLabelPreview(true);
      });
    } else {
      setEnableLabelPreview(false);
    }
  };

  const handleChangeBartenderServers = (newBartenderServers: Array<BartenderServer>): void => {
    writeQuery(
      {query: LABEL_PRINTING_SETTINGS_QUERY},
      {
        ...conf,
        labelPrintingConfig: {
          ...conf.labelPrintingConfig,
          bartenderServers: newBartenderServers.map((bs) => ({
            ...bs,
            __typename: 'BartenderServerNode',
            // We don't have an ID from the backend yet, but we need one to write this data to the graphql
            // cache. Since this is a fabricated ID it is possible to make it match the one produced by the
            // backend, though I'm not 100% sure that's necessary. To be safe though, keep this in sync with
            // benchling.label_printing.graphql.graphql_resolvers.BartenderServerNode.resolve_id()
            id: `bartender_server-${conf.currentTenant.name}-${bs.url}`,
          })),
        },
      }
    );
    onPersistChanges({
      labelPrintingMethod: tenantConfiguredPrintingMethod,
      bartenderServers: newBartenderServers.map((bs) => makePlainBartenderServer(bs)),
      enableLabelTemplatePreview,
    });
  };

  return (
    <>
      <Row>
        <Col md={5}>
          <div className={css(styles.marginTop24px)}>
            <FieldLabel label="Print method" />
          </div>
          <EditablePrintMethod
            persistedPrintMethod={tenantConfiguredPrintingMethod}
            isWritable={isWritable}
            onChange={handleChangePrintingMethod}
          />
        </Col>
      </Row>
      {tenantConfiguredPrintingMethod === LabelPrintingMethod.BARTENDER ? (
        <div className={css(styles.marginTop18px)}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="BarTender runs on your local network."
            supportingInfo={
              <>
                All communication with BarTender happens between your browser and the local BarTender server.
                This means your information and your printers aren’t accessible to Benchling’s servers.{' '}
                <a
                  href="https://help.benchling.com/hc/articles/9684240594573"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </>
            }
            action={{actionType: InlineBannerActionType.ACCORDION}}
          />
          <div className={css(styles.marginTop24px)}>
            <BartenderServerTable
              bartenderServers={bartenderServers}
              isWritable={isWritable}
              onChange={handleChangeBartenderServers}
            />
          </div>
        </div>
      ) : (
        <div className={css(styles.flexMarginTop18px)}>
          <FieldLabel
            grayOutForeground={!isWritable}
            label="Enable label template preview"
            className="u-mini-spacing-right u-mini-spacing-top u-normal-font-weight"
          />
          <HelpButton
            renderContent={() => (
              <p>
                Enabling this feature allows a third-party party application to generate image previews of ZPL
                label templates.
              </p>
            )}
            iconClassName="u-mini-spacing-right"
            interaction={FloatingInteraction.HOVER}
          />
          <ToggleGroup
            options={[
              {id: LabelTemplatePreviewStatus.ON, buttonProps: {text: 'ON'}},
              {id: LabelTemplatePreviewStatus.OFF, buttonProps: {text: 'OFF'}},
            ]}
            size={ButtonSize.MINI}
            selection={
              enableLabelTemplatePreview ? LabelTemplatePreviewStatus.ON : LabelTemplatePreviewStatus.OFF
            }
            onChangeSelection={handleChangeLabelPreview}
            disabled={!isWritable}
          />
        </div>
      )}
    </>
  );
}

function LabelPrintingSettingsAuditLogsButton({
  conf,
}: {
  conf: LabelPrintingSettingsQuery;
}): JSX.Element | null {
  const isTenantAdminDataNode = (
    tenantAdminDataOrInaccessible: TenantAdminDataOrInaccessibleInfoNode
  ): tenantAdminDataOrInaccessible is TenantAdminDataNode =>
    tenantAdminDataOrInaccessible.__typename === 'TenantAdminDataNode';

  const labelPrintingConfigOverrideIds = isTenantAdminDataNode(conf.currentTenant.tenantAdminData)
    ? conf.currentTenant.tenantAdminData.configOverrides.map(({id}) => id)
    : null;

  if (labelPrintingConfigOverrideIds == null) {
    return null;
  }

  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
          <Option
            icon="audit-trail"
            onSelect={() => {
              AuditActions.exportAuditLogs(labelPrintingConfigOverrideIds);
            }}
          >
            {EXPORT_AUDIT_LOG_DISPLAY_STRING}
          </Option>
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        isDropdown={true}
        size={ButtonSize.SMALL}
        text="Export"
      />
    </Dropdown>
  );
}

export default function LabelPrintingSettingsPage(): JSX.Element {
  setWindowTitle('Label Printing Settings');

  const queryResult = useBenchlingQuery<LabelPrintingSettingsQuery>(LABEL_PRINTING_SETTINGS_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  const handlePersistChanges = async (settings: {
    labelPrintingMethod: LabelPrintingMethod;
    bartenderServers: Array<BartenderServer>;
    enableLabelTemplatePreview: Boolean;
  }): Promise<void> => {
    try {
      await putLabelPrintingConfig(settings);
      notifications.success('Label printing settings successfully updated.');
    } finally {
      queryResult.refetch();
    }
  };

  let mainContent: JSX.Element;
  let auditLogsButton: JSX.Element | null = null;

  if (queryResult.type === QueryResultType.ERROR) {
    const message = 'Unable to load label printing configuration. Please try again.';
    notifications.error(message);
    mainContent = <p>{message}</p>;
  } else if (queryResult.type === QueryResultType.SUCCESS) {
    mainContent = (
      <LabelPrintingSettingsPageContent conf={queryResult.data} onPersistChanges={handlePersistChanges} />
    );
    auditLogsButton = <LabelPrintingSettingsAuditLogsButton conf={queryResult.data} />;
  } else {
    mainContent = <HorizontalThrobber />;
  }

  return (
    <div className="settingsPageContainer-scrollContainer">
      <div className="settingsPage">
        <div className="settingsPage-contentWrapper">
          <div className="u-lr-split">
            <Header1 className={classNames('settingsPage-header', css(styles.headerMargin))}>
              Label printing
            </Header1>
            {auditLogsButton}
          </div>
          <p>
            Configure a label printing method for your tenant. This will take precedence over any label
            printing settings in your registry.{' '}
            <a
              href="https://help.benchling.com/hc/articles/9684247711373"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>
          </p>
          {mainContent}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerMargin: {
    marginBottom: '8px',
  },
  marginTop18px: {
    marginTop: '18px',
  },
  marginTop24px: {
    marginTop: '24px',
  },
  flexMarginTop18px: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '18px',
  },
  disabledColor: {
    color: GRAY_DARK,
  },
});
